import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, GraduationCap, ChevronRight, BookOpen, Sparkles } from 'lucide-react';
import { Card } from '../components/ui/card';
import { API } from '../context/AuthContext';

const MEDIA = [{ code: 'odia', label: 'ଓଡ଼ିଆ' }, { code: 'english', label: 'English' }];

export default function CurriculumPage() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [medium, setMedium] = useState('odia');
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/curriculum/classes`).then(r => setClasses(r.data)).catch(() => setClasses([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedClass) {
      setSelectedSubject(null);
      setChapters([]);
      axios.get(`${API}/curriculum/subjects`, { params: { class_num: selectedClass, medium } }).then(r => setSubjects(r.data)).catch(() => setSubjects([]));
    }
  }, [selectedClass, medium]);

  useEffect(() => {
    if (selectedClass && selectedSubject) {
      axios.get(`${API}/curriculum/chapters`, { params: { class_num: selectedClass, subject_key: selectedSubject, medium } }).then(r => setChapters(r.data)).catch(() => setChapters([]));
    }
  }, [selectedSubject, selectedClass, medium]);

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium" data-testid="curriculum-back">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #059669 0%, #0891b2 60%, #7c3aed 100%)' }}>
        <GraduationCap size={26} />
        <h1 className="text-xl font-extrabold mt-2">📚 Odisha School Curriculum</h1>
        <p className="text-xs text-white/90 mt-1">Class 1-10 · Odia + English Medium · SCERT-aligned</p>
      </div>

      {/* Medium toggle */}
      <div className="flex gap-2">
        {MEDIA.map(m => (
          <button key={m.code} onClick={() => setMedium(m.code)} data-testid={`medium-${m.code}`}
            className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${medium === m.code ? 'bg-emerald-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-700'}`}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Class grid */}
      {!selectedClass && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {loading ? <div className="col-span-3 text-center text-sm text-gray-500 py-8">Loading...</div> :
            classes.map(c => (
              <button key={c.class} onClick={() => setSelectedClass(c.class)} data-testid={`class-${c.class}`}
                className="p-3 rounded-2xl border-2 border-gray-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 text-center transition-all">
                <div className="text-2xl font-extrabold text-emerald-700">{c.class}</div>
                <div className="text-[10px] text-gray-600 mt-0.5">Class {c.class}</div>
                <div className="text-[9px] text-gray-400 mt-0.5">{c.subjects.length > 0 ? `${c.subjects.length} subjects` : 'Pending'}</div>
              </button>
            ))}
        </div>
      )}

      {/* Subjects list */}
      {selectedClass && !selectedSubject && (
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedClass(null)} className="text-xs text-gray-500 hover:text-emerald-600" data-testid="back-to-classes">← Classes</button>
            <span className="text-sm font-bold text-gray-900">Class {selectedClass} · {medium === 'odia' ? 'ଓଡ଼ିଆ Medium' : 'English Medium'}</span>
          </div>
          {subjects.length === 0 ? (
            <Card className="rounded-2xl p-6 border-amber-200 bg-amber-50 text-center">
              <div className="text-2xl">⏳</div>
              <div className="font-bold text-amber-800 mt-2">Curriculum ଅପେକ୍ଷାରତ</div>
              <p className="text-xs text-amber-700 mt-1">ଏହି class ପାଇଁ curriculum ଏଯାଏଁ upload ହୋଇ ନାହିଁ। Admin ଶୀଘ୍ର ଯୋଡ଼ିବେ।</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {subjects.map(s => (
                <button key={s.subject_key} onClick={() => setSelectedSubject(s.subject_key)} data-testid={`subject-${s.subject_key}`}
                  className="w-full text-left p-4 rounded-2xl border-2 border-gray-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 transition-all flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <BookOpen size={18} className="text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900">{s.subject_name_odia}</div>
                    <div className="text-[11px] text-gray-500">{s.subject_name_en} · {s.chapters_count} chapters</div>
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Chapters + Topics */}
      {selectedClass && selectedSubject && (
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedSubject(null)} className="text-xs text-gray-500 hover:text-emerald-600" data-testid="back-to-subjects">← Subjects</button>
            <span className="text-sm font-bold text-gray-900">Class {selectedClass} · {selectedSubject}</span>
          </div>
          <div className="space-y-2">
            {chapters.map((c, idx) => (
              <details key={c.id} className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden" data-testid={`chapter-${idx}`}>
                <summary className="p-4 cursor-pointer flex items-center gap-3 hover:bg-emerald-50">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 text-emerald-700 font-extrabold text-sm">{c.chapter_number}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900 truncate">{c.chapter_title_odia}</div>
                    <div className="text-[11px] text-gray-500 truncate">{c.chapter_title_en} · {c.topics?.length || 0} topics</div>
                  </div>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold shrink-0 ${c.verification_status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {c.verification_status === 'verified' ? '✓ Verified' : 'Placeholder'}
                  </span>
                </summary>
                <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50">
                  {(c.topics || []).map((t, ti) => (
                    <div key={ti} className="bg-white rounded-xl p-3 border border-gray-200">
                      <div className="font-semibold text-sm text-gray-900">{t.title_odia}</div>
                      <div className="text-[11px] text-gray-500 mb-2">{t.title_en}</div>
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div><span className="font-bold text-gray-600">DG:</span> <span className="text-gray-700">{t.dg || '—'}</span></div>
                        <div><span className="font-bold text-gray-600">CG:</span> <span className="text-gray-700">{t.cg || '—'}</span></div>
                        <div><span className="font-bold text-gray-600">LO:</span> <span className="text-gray-700">{t.lo || '—'}</span></div>
                        <div><span className="font-bold text-gray-600">LO Code:</span> <span className="text-gray-700 font-mono">{t.lo_code || '—'}</span></div>
                      </div>
                      {t.note && !t.verified && (
                        <p className="text-[10px] text-amber-700 mt-2 bg-amber-50 p-2 rounded">⚠ {t.note}</p>
                      )}
                      <div className="mt-2 flex gap-2">
                        <Link to={`/lesson-plan?class=${selectedClass}&subject=${encodeURIComponent(c.subject_name_odia)}&chapter=${encodeURIComponent(c.chapter_title_odia)}&topic=${encodeURIComponent(t.title_odia)}&dg=${encodeURIComponent(t.dg || '')}&cg=${encodeURIComponent(t.cg || '')}&lo=${encodeURIComponent(t.lo || '')}&lo_code=${encodeURIComponent(t.lo_code || '')}`}
                          className="text-[10px] bg-purple-600 text-white px-2 py-1 rounded-lg font-bold hover:bg-purple-700">
                          <Sparkles size={10} className="inline mr-1" /> AI Lesson Plan
                        </Link>
                      </div>
                    </div>
                  ))}
                  {(!c.topics || c.topics.length === 0) && (
                    <p className="text-xs text-gray-500 italic">No topics defined. Admin can add topics.</p>
                  )}
                </div>
              </details>
            ))}
          </div>
        </>
      )}

      <div className="text-center text-[10px] text-gray-400 pt-2">
        Source: <a href="https://scertodisha.nic.in/" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">SCERT Odisha</a> · Placeholder data — Admin will verify
      </div>
    </div>
  );
}
